import { authStore } from '~/stores/auth'
import { companyStore } from '~/stores/company'
import api from './api'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api'

export type AICategoryType = 'chat' | 'image' | 'audio' | 'document' | 'embedding'
/** No es un union fijo: los proveedores los declara el backend en su catálogo. */
export type AIProviderNameType = string

export interface ChatMessageType {
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  toolCallId?: string
}

export interface ChatResponseType {
  content: string
  toolCalls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }>
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number }
}

/** La apiKey llega siempre enmascarada; `hasApiKey` dice si hay una guardada. */
export interface AIProviderConfigType {
  id: number
  category: AICategoryType
  provider: AIProviderNameType
  model: string
  /** Qué conexión (credenciales) usa esta capacidad. */
  connectionId: number | null
  apiKey: string | null
  hasApiKey: boolean
  baseUrl: string | null
  isActive: number | null
}

/** Resultado de la última comprobación de una conexión contra su proveedor. */
export type ConnectionStatusType = 'unverified' | 'ok' | 'error'

/**
 * Una cuenta concreta de un proveedor. Una empresa puede tener varias del mismo
 * proveedor ("Gemini producción", "Gemini del cliente A"): lo que las distingue
 * es el `label`, no el proveedor. La apiKey llega siempre enmascarada.
 */
export interface AIConnectionType {
  id: number
  provider: AIProviderNameType
  label: string
  apiKey: string | null
  hasApiKey: boolean
  baseUrl: string | null
  status: ConnectionStatusType
  lastValidatedAt: string | null
  lastError: string | null
  isActive: number | null
}

/** Un modelo al que llega una conexión concreta. */
export interface AIConnectionModelType {
  id: number
  connectionId: number
  model: string
  /** El proveedor lo sigue ofreciendo. false = lo retiraron. */
  available: boolean
  enabled: boolean
  lastSeenAt: string | null
}

/** Lo que responde "probar conexión". */
export interface ConnectionTestResultType {
  ok: boolean
  error: string | null
  /** Cuántos modelos devolvió el proveedor. */
  models: number
}

export type AgentScopeType = 'internal' | 'public' | 'both'
export type AgentTypeType = 'text' | 'voice'

export interface AIAgentType {
  id: number
  companyId: string
  name: string
  slug: string
  description?: string | null
  systemPrompt: string
  model?: string | null
  temperature?: number | null
  maxTokens?: number | null
  tools?: string[] | null
  isDefault?: boolean | null
  /** Emoji o icono para reconocerlo en el chat. */
  avatar?: string | null
  /** internal = sólo el panel; public = sólo la web; both = ambos. */
  scope?: AgentScopeType
  /** text = responde por el chat; voice = responde con audio (Gemini Live). */
  type?: AgentTypeType
  /** Con qué conexión (proveedor) habla; null = el por defecto de su categoría. */
  connectionId?: number | null
  /** Si su modelo acepta imágenes (visión). Se marca a mano: la API no lo dice. */
  acceptsImages?: boolean | null
  status?: number | null
}

export interface AIToolType {
  name: string
  description: string
  requiresGodMode: boolean
}

/**
 * Eventos del stream. `tool_call`/`tool_result` permiten mostrar qué está
 * haciendo el asistente en vez de dejar la burbuja muda mientras trabaja.
 */
export type StreamEventType = { kind: 'content'; text: string } | { kind: 'tool_call'; tool: string; arguments?: Record<string, unknown> } | { kind: 'tool_result'; tool: string; error?: string }

export interface PublicChatConfigType {
  enabled: boolean
  name?: string
  greeting?: string
  avatar?: string | null
  /** Si hay asesor de texto (chat) disponible. */
  chat?: boolean
  /** Si hay asesor de voz publicado (muestra el botón de llamar). */
  voice?: boolean
}

/** Una capacidad ahora usa una CONEXIÓN; provider/apiKey/baseUrl es el flujo antiguo. */
export interface SaveProviderPayloadType {
  connectionId?: number
  provider?: AIProviderNameType
  model: string
  /** Vacía = conserva la guardada en el servidor. */
  apiKey?: string
  baseUrl?: string
  isActive?: number
}

export interface CreateConnectionPayloadType {
  provider: AIProviderNameType
  label?: string
  apiKey?: string
  baseUrl?: string
}

/** El proveedor no está: una conexión no puede cambiar de servicio. */
export interface UpdateConnectionPayloadType {
  label?: string
  /** Vacía = conserva la guardada en el servidor. */
  apiKey?: string
  baseUrl?: string
}

export interface SaveAgentPayloadType {
  name: string
  slug?: string
  description?: string
  systemPrompt: string
  model?: string | null
  temperature?: number | null
  maxTokens?: number | null
  tools?: string[] | null
  isDefault?: boolean
  avatar?: string | null
  scope?: AgentScopeType
  type?: AgentTypeType
  connectionId?: number | null
  acceptsImages?: boolean
  status?: number
}

/** Qué proveedores existen y qué categorías implementa cada uno. */
export interface ProviderCatalogEntryType {
  name: string
  label: string
  supportsBaseUrl: boolean
  requiresBaseUrl: boolean
  /** Si el proveedor trae una, el campo es opcional y esta es la que se usa. */
  defaultBaseUrl: string | null
  /** Los locales (Ollama) no tienen cuenta ni claves. */
  requiresApiKey: boolean
  notes: string | null
  categories: AICategoryType[]
  defaultModels: Partial<Record<AICategoryType, string>>
  /** Si el proveedor puede decirnos qué modelos ofrece. */
  canListModels: boolean
}

/** Capacidad habilitada/deshabilitada por proveedor y empresa. */
export interface AIProviderCapabilityType {
  id: number
  companyId: string | null
  provider: string
  category: string
  enabled: number | null
}

export const aiService = {
  /** El catálogo lo declara el backend: la UI sólo ofrece combinaciones válidas. */
  async providerCatalog() {
    const { data } = await api.get('/ai/providers/catalog')
    return data.data as ProviderCatalogEntryType[]
  },

  // ── Configuración de proveedores ───────────────────────────────────────
  async listProviders() {
    const { data } = await api.get('/ai/providers')
    return data.data as AIProviderConfigType[]
  },

  async saveProvider(category: AICategoryType, payload: SaveProviderPayloadType) {
    const { data } = await api.put(`/ai/providers/${category}`, payload)
    return data.data as AIProviderConfigType
  },

  /**
   * Pregunta al proveedor qué modelos ofrece. Se le pasa la conexión (y así no se
   * reenvía la clave) o el proveedor con credenciales sueltas mientras se rellena.
   */
  async listModels(category: AICategoryType, payload: { connectionId?: number; provider?: string; apiKey?: string; baseUrl?: string }) {
    const { data } = await api.post(`/ai/providers/${category}/models`, payload)
    return data.data as string[]
  },

  async deleteProvider(category: AICategoryType) {
    const { data } = await api.delete(`/ai/providers/${category}`)
    return data.data as boolean
  },

  // ── Conexiones (credenciales por proveedor) ────────────────────────────
  async listConnections() {
    const { data } = await api.get('/ai/connections')
    return data.data as AIConnectionType[]
  },

  async createConnection(payload: CreateConnectionPayloadType) {
    const { data } = await api.post('/ai/connections', payload)
    return data.data as AIConnectionType
  },

  async updateConnection(id: number, payload: UpdateConnectionPayloadType) {
    const { data } = await api.put(`/ai/connections/${id}`, payload)
    return data.data as AIConnectionType
  },

  async deleteConnection(id: number) {
    const { data } = await api.delete(`/ai/connections/${id}`)
    return data.data as boolean
  },

  /**
   * Comprueba la conexión contra su proveedor y, si responde, refresca sus
   * modelos. El resultado queda guardado en la conexión, así que después basta
   * con recargar la lista para verlo.
   */
  async testConnection(id: number) {
    const { data } = await api.post(`/ai/connections/${id}/test`)
    return data.data as ConnectionTestResultType
  },

  /** Modelos guardados de una conexión. */
  async connectionModels(id: number) {
    const { data } = await api.get(`/ai/connections/${id}/models`)
    return data.data as AIConnectionModelType[]
  },

  /** Modelos de todas las conexiones de la empresa, de una tirada. */
  async allConnectionModels() {
    const { data } = await api.get('/ai/connection-models')
    return data.data as AIConnectionModelType[]
  },

  // ── Agentes ────────────────────────────────────────────────────────────
  async listAgents() {
    const { data } = await api.get('/ai/agents')
    return data.data as AIAgentType[]
  },

  async createAgent(payload: SaveAgentPayloadType) {
    const { data } = await api.post('/ai/agents', payload)
    return data.data as AIAgentType
  },

  async updateAgent(id: number, payload: SaveAgentPayloadType) {
    const { data } = await api.put(`/ai/agents/${id}`, payload)
    return data.data as AIAgentType
  },

  async deleteAgent(id: number) {
    const { data } = await api.delete(`/ai/agents/${id}`)
    return data.data as boolean
  },

  // ── Tools ──────────────────────────────────────────────────────────────
  async listTools() {
    const { data } = await api.get('/ai/tools')
    return data.data as AIToolType[]
  },

  // ── Chat ───────────────────────────────────────────────────────────────
  async chat(messages: ChatMessageType[], agentId?: number) {
    const { data } = await api.post('/ai/chat', { messages, agentId })
    return data.data as ChatResponseType
  },

  /**
   * Chat del panel con streaming. Requiere sesión.
   * `onEvent` recibe texto y también los eventos de tools; devuelve el texto
   * completo al terminar.
   */
  async chatStream(messages: ChatMessageType[], onEvent: (event: StreamEventType) => void, options: { agentId?: number; images?: string[]; signal?: AbortSignal } = {}): Promise<string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (authStore.token) headers.Authorization = `Bearer ${authStore.token}`
    if (companyStore.id) headers['X-Company-ID'] = companyStore.id

    return readSseStream(`${API_URL}/ai/chat/stream`, headers, { messages, agentId: options.agentId, ...(options.images?.length ? { images: options.images } : {}) }, onEvent, options.signal, true)
  },

  // ── Chat público (sin sesión) ──────────────────────────────────────────
  async publicConfig() {
    const { data } = await api.get('/ai/public/config')
    return data.data as PublicChatConfigType
  },

  /** Asesor de la web pública: sin token ni empresa, el backend los resuelve por dominio. */
  async publicChatStream(messages: Array<{ role: 'user' | 'assistant'; content: string }>, onEvent: (event: StreamEventType) => void, options: { signal?: AbortSignal } = {}): Promise<string> {
    return readSseStream(`${API_URL}/ai/public/chat/stream`, { 'Content-Type': 'application/json' }, { messages }, onEvent, options.signal, false)
  },

  // ── Capacidades por proveedor y empresa ──────────────────────────────────
  async listCapabilities() {
    const { data } = await api.get('/ai/provider-capabilities')
    return data.data as AIProviderCapabilityType[]
  },

  async toggleCapability(provider: string, category: string, enabled: boolean) {
    const { data } = await api.put('/ai/provider-capabilities', { provider, category, enabled })
    return data.data as AIProviderCapabilityType
  }
}

/** Lo que emite el backend en cada frame `data:` del stream. */
interface SseFrameType {
  kind?: 'content' | 'tool_call' | 'tool_result'
  content?: string
  finishReason?: string
  tool?: { name?: string; arguments?: Record<string, unknown>; error?: string }
  error?: string
}

/**
 * Lee un stream SSE de un POST. No se puede usar EventSource: sólo hace GET y
 * aquí el cuerpo va en JSON.
 */
async function readSseStream(
  url: string,
  headers: Record<string, string>,
  body: unknown,
  onEvent: (event: StreamEventType) => void,
  signal: AbortSignal | undefined,
  redirectOn401: boolean
): Promise<string> {
  const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body), signal })

  if (!response.ok && response.status !== 429) {
    // El interceptor de axios no cubre fetch: replicamos su comportamiento en 401.
    if (redirectOn401 && response.status === 401 && authStore.token) window.location.href = '/login'
    const payload = await response.json().catch(() => null)
    throw new Error(payload?.message ?? `Error ${response.status}`)
  }
  if (!response.body) throw new Error('El servidor no devolvió un stream')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let full = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    // Los frames SSE se separan por línea en blanco; un chunk de red puede
    // partir un frame, así que el resto se conserva para la siguiente vuelta.
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''

    for (const frame of frames) {
      const line = frame.trim()
      if (!line.startsWith('data:')) continue

      const payload = line.slice(5).trim()
      if (payload === '[DONE]') return full

      let parsed: SseFrameType
      try {
        parsed = JSON.parse(payload) as SseFrameType
      } catch {
        continue // frame partido o ruido: se ignora
      }

      // El error del servidor viaja dentro del stream (el status ya era 200).
      if (parsed.error) throw new Error(parsed.error)

      if (parsed.kind === 'tool_call') {
        onEvent({ kind: 'tool_call', tool: parsed.tool?.name ?? '', arguments: parsed.tool?.arguments })
      } else if (parsed.kind === 'tool_result') {
        onEvent({ kind: 'tool_result', tool: parsed.tool?.name ?? '', error: parsed.tool?.error })
      } else if (parsed.content) {
        full += parsed.content
        onEvent({ kind: 'content', text: parsed.content })
      }
    }
  }

  return full
}

export default aiService
